import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api } from "../api";
import { addToCart } from "../lib/cart";
import { CartDrawer } from "../components/ShopTab";
import { ShareMenu } from "../components/ShareMenu";
import { SmartBack } from "../components/SmartBack";
import { useSocialMeta } from "../lib/socialMeta";
import { toast } from "sonner";
import i18n from "../i18n";

const eur = (c) => new Intl.NumberFormat(i18n.language || "it", { style: "currency", currency: "EUR" }).format((Number.isFinite(c) ? c : 0) / 100);
const IMG_BASE = process.env.REACT_APP_BACKEND_URL || "";
const imgSrc = (s) => (s && s.startsWith("/api/") ? IMG_BASE + s : s);
const hasRealVariants = (p) => Array.isArray(p?.variants) && p.variants.length > 0;

export default function ProductDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [opts, setOpts] = useState({});
  const [qty, setQty] = useState(1);
  const [cartOpen, setCartOpen] = useState(false);
  useEffect(() => { api.get(`/products/${id}`).then(({ data }) => setP(data)).catch(() => setErr(true)); }, [id]);
  useSocialMeta(p ? {
    title: `${p.name} — ${p.org_name || "YouPAM"}`,
    description: [p.org_name, p.description].filter(Boolean).join(" · ").slice(0, 180),
    image: imgSrc(p.images?.[0]), url: `${window.location.origin}/products/${id}`, type: "article",
  } : null);
  if (err) return <Layout><div className="yp-container py-8"><SmartBack fallbackTo="/search?type=products" label={t("shop.title")} /><div className="text-red-600" data-testid="product-error">{t("shop.unavailable")}</div></div></Layout>;
  if (!p) return <Layout><div className="yp-container py-8" data-testid="product-loading">{t("common.loading")}</div></Layout>;
  const variants = hasRealVariants(p);
  const attrs = variants ? [...new Set(p.variants.flatMap((v) => Object.keys(v.options || {})))] : [];
  const matched = variants ? p.variants.find((v) => attrs.every((a) => (v.options || {})[a] === opts[a])) : null;
  const price = variants ? (matched ? matched.eff_price_cents : Math.min(...p.variants.map(v => v.eff_price_cents).filter(Number.isFinite))) : (Number.isFinite(p.eff_price_cents) ? p.eff_price_cents : 0);
  const avail = variants ? (matched ? matched.available : null) : p.available;
  const add = () => {
    if (variants && !matched) { toast.error(t("shop.select_variant")); return; }
    if (avail !== null && qty > avail) { toast.error(t("shop.out_of_stock")); return; }
    const item = { product_id: p.id, variant_id: matched?.id || null, product_name: p.name, variant_label: matched?.label || "", unit_price_cents: price, image: matched?.image || p.images?.[0] || null, qty };
    const r = addToCart(p.org_id, p.org_name, item);
    if (r.conflict) { if (window.confirm(t("shop.vendor_conflict", { org: r.currentOrg }))) { addToCart(p.org_id, p.org_name, item, true); toast.success(t("shop.added")); setCartOpen(true); } return; }
    toast.success(t("shop.added")); setCartOpen(true);
  };
  return (<Layout><div className="yp-container max-w-3xl py-8" data-testid="product-detail-page">
    <SmartBack fallbackTo={`/organizations/${p.org_id}?tab=shop`} label={t("shop.title")} />
    <div className="yp-card p-6 yp-gradient-border">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h1 className="text-3xl font-bold" data-testid="product-title">{p.name}</h1>
        <ShareMenu shareUrl={`${window.location.origin}/p/prodotto-${id}`} title={p.name} />
      </div>
      <Link to={`/organizations/${p.org_id}`} className="inline-block mt-2 text-blue-700 font-medium hover:underline" data-testid="product-org-link">{p.org_name}</Link>
      {p.images?.[0] && <img src={imgSrc(p.images[0])} alt={p.name} className="w-full max-h-96 object-contain rounded my-4 bg-slate-50" data-testid="product-image" />}
      {p.category && <div className="text-xs text-slate-500" data-testid="product-category">{p.category}</div>}
      {p.description && <p className="mt-2 text-slate-700 whitespace-pre-wrap" data-testid="product-desc">{p.description}</p>}
      <div className="text-2xl font-semibold mt-3" data-testid="product-price">{eur(price)}</div>
      {attrs.map((a) => (
        <div key={a} className="mt-3">
          <label className="text-sm font-medium capitalize">{a}</label>
          <div className="flex gap-2 flex-wrap mt-1">
            {[...new Set(p.variants.map((v) => (v.options || {})[a]).filter(Boolean))].map((val) => (
              <button key={val} onClick={() => setOpts({ ...opts, [a]: val })} data-testid="product-variant-option"
                className={`yp-btn !py-1 !px-3 text-xs ${opts[a] === val ? "yp-btn-primary" : "yp-btn-outline"}`}>{val}</button>))}
          </div>
        </div>))}
      {avail !== null && <div className="text-xs text-slate-500 mt-2" data-testid="product-avail">{avail > 0 ? t("shop.in_stock", { n: avail }) : t("shop.out_of_stock")}</div>}
      <div className="flex items-center gap-2 mt-3">
        <label className="text-sm">{t("shop.qty")}</label>
        <input type="number" min="1" value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value)))} className="yp-input !w-20 !py-1" data-testid="product-qty-input" />
      </div>
      <button className="yp-btn yp-btn-primary w-full mt-4" onClick={add} disabled={avail === 0} data-testid="product-add-to-cart">{t("shop.add_to_cart")}</button>
    </div>
    {cartOpen && <CartDrawer onClose={() => setCartOpen(false)} />}
  </div></Layout>);
}
